import React, { useState, useEffect } from "react";

import { Trans, useTranslation } from "react-i18next";
import Select from "react-select";
import {
    getReactSelectTheme,
    getReactSelectSmallStyling,
} from "../../utils/ReactSelectStyles";
import LookupsAPI from "../../API/lookups.api";
import ValidationError from "../../components/Shared/ValidationError";
import { cloneDeep } from "lodash";
import { MAX_PARAMETER } from "../../utils/constants";

const ParameterConstraints = ({
    materialId,
    parameterConstraints,
    setParameterConstraints,
    isFormSubmitted,
    error,
}) => {
    //General states
    const { t } = useTranslation();

    //Data states
    const [parametersLookup, setParametersLookup] = useState([]);

    //Use effects
    useEffect(() => {
        if (materialId) {
            getParametersLookup(materialId);
        }
    }, [materialId]);

    //Data fetching
    const getParametersLookup = async (materialId) => {
        const result = await LookupsAPI.getModelParameterNamesByMaterial(
            materialId,
            true
        );
        if (result) setParametersLookup(result);
    };

    //Handlers
    const handleParameterFieldsChange = (e, index) => {
        const newParameterConstrainsts = cloneDeep(parameterConstraints);
        if (e.target?.type === "number") {
            if (e.target.value)
                newParameterConstrainsts[index] = {
                    ...newParameterConstrainsts[index],
                    [e.target.name]: e.target.value,
                };
            else delete newParameterConstrainsts[index]?.[e.target.name];
            setParameterConstraints(newParameterConstrainsts);
        } else {
            newParameterConstrainsts[index] = {
                ...newParameterConstrainsts[index],
                parameter: e,
            };
            setParameterConstraints(newParameterConstrainsts);
        }
    };
    const removeParameterCard = (index) => {
        const newParameterConstrainsts = cloneDeep(parameterConstraints);
        newParameterConstrainsts.splice(index, 1);
        setParameterConstraints(newParameterConstrainsts);
    };

    const getParametersOptions = () => {
        const parameterOptions = [];
        parametersLookup.forEach((elem) => {
            if (
                !parameterConstraints.find(
                    (param) => param?.parameter?.value === elem?.value
                )
            )
                parameterOptions.push(elem);
        });
        return parameterOptions;
    };

    //Renderers
    const renderParameterFields = () => {
        return (
            <>
                <div className="input-group w-full">
                    <div className="tags whitespace-wrap">
                        <h4 className="uppercase text-xs required">
                            <Trans i18nKey="alertConstraints">
                                Alert Constraints
                            </Trans>
                        </h4>
                    </div>
                    {parameterConstraints.map((elem, index) => (
                        <div
                            className="item-list orange-list no-indentation mb-2"
                            key={index}
                        >
                            <div className="flex justify-between align-center">
                                <div className="input-group w-full">
                                    <div className="tags px-2 py-2 whitespace-wrap">
                                        <h4 className="uppercase text-xs">
                                            <Trans i18nKey="parameter">
                                                parameter
                                            </Trans>
                                        </h4>
                                        <Select
                                            options={getParametersOptions()}
                                            value={elem.parameter || ""}
                                            onChange={(val) =>
                                                handleParameterFieldsChange(
                                                    val,
                                                    index
                                                )
                                            }
                                            placeholder={t("parameter")}
                                            styles={getReactSelectSmallStyling(
                                                false
                                            )}
                                            theme={getReactSelectTheme}
                                            // components={getShortSelectMenu()}
                                        />
                                        {isFormSubmitted && (
                                            <ValidationError
                                                errorMessage={
                                                    error[
                                                        `parameterConstraints.${index}.parameter`
                                                    ]
                                                }
                                            />
                                        )}
                                    </div>
                                    {isFormSubmitted && (
                                        <ValidationError
                                            errorMessage={
                                                error[
                                                    `parameter.${elem?.parameter?.value}.exist`
                                                ]
                                            }
                                        />
                                    )}
                                </div>
                                <div className="input-group w-full">
                                    <div className="tags px-2 py-2 whitespace-wrap">
                                        <h4 className="uppercase text-xs">
                                            <Trans i18nKey="min">Min</Trans>
                                        </h4>
                                        <input
                                            className="input-element small-input"
                                            type="number"
                                            name="min"
                                            min="0"
                                            value={elem.min ?? ""}
                                            onChange={(e) =>
                                                handleParameterFieldsChange(
                                                    e,
                                                    index
                                                )
                                            }
                                            placeholder={t(
                                                "decimalPlacesPlaceholder"
                                            )}
                                        />
                                        {isFormSubmitted && (
                                            <ValidationError
                                                errorMessage={
                                                    error[
                                                        `parameterConstraints.${index}.min`
                                                    ]
                                                }
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="input-group w-full">
                                    <div className="tags px-2 py-2 whitespace-wrap">
                                        <h4 className="uppercase text-xs">
                                            <Trans i18nKey="max">Max</Trans>
                                        </h4>
                                        <input
                                            className="input-element small-input"
                                            type="number"
                                            name="max"
                                            min="0"
                                            value={elem.max ?? ""}
                                            onChange={(e) =>
                                                handleParameterFieldsChange(
                                                    e,
                                                    index
                                                )
                                            }
                                            placeholder={t(
                                                "decimalPlacesPlaceholder"
                                            )}
                                        />
                                        {isFormSubmitted && (
                                            <ValidationError
                                                errorMessage={
                                                    error[
                                                        `parameterConstraints.${index}.max`
                                                    ]
                                                }
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="input-group w-full">
                                    <div className="tags px-2 py-2 whitespace-wrap">
                                        <h4 className="uppercase text-xs">
                                            <Trans i18nKey="equals">
                                                Equals
                                            </Trans>
                                        </h4>
                                        <input
                                            className="input-element small-input"
                                            type="number"
                                            name="equals"
                                            min="0"
                                            value={elem.equals ?? ""}
                                            onChange={(e) =>
                                                handleParameterFieldsChange(
                                                    e,
                                                    index
                                                )
                                            }
                                            placeholder={t(
                                                "decimalPlacesPlaceholder"
                                            )}
                                        />
                                        {isFormSubmitted && (
                                            <ValidationError
                                                errorMessage={
                                                    error[
                                                        `parameterConstraints.${index}.equals`
                                                    ]
                                                }
                                            />
                                        )}
                                    </div>
                                </div>
                                {parameterConstraints.length > 1 && (
                                    <div className="icon-wrapper text-xs mt-1">
                                        <i
                                            className="fas fa-times cursor-pointer"
                                            onClick={() =>
                                                removeParameterCard(index)
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                            {isFormSubmitted && (
                                <ValidationError
                                    customClassNames="m-0"
                                    errorMessage={
                                        error[
                                            `parameterConstraints.${index}`
                                        ] &&
                                        "Parameter Constraints must have atleast 1 constraint"
                                    }
                                />
                            )}
                        </div>
                    ))}

                    <div className="flex justify-end mr-4 p-2">
                        <span
                            onClick={() =>
                                setParameterConstraints((prevData) => [
                                    ...prevData,
                                    {},
                                ])
                            }
                            className={`${
                                parameterConstraints.length > MAX_PARAMETER
                                    ? "disabled"
                                    : ""
                            } hyper-btn text-xs`}
                        >
                            <i className="fas fa-plus fa-xs mr-1"></i>
                            <Trans i18nKey="addParameter">Add Parameter</Trans>
                        </span>
                    </div>
                </div>
            </>
        );
    };
    return <>{renderParameterFields()}</>;
};

export default ParameterConstraints;
